import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { IRecentEventsTableRow } from "../common/events.models";
import { MITIGATED_RISKS } from "../common/events.constants";
import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { checkIfEventPast7Days } from "../../../../../common/utils/checkIfEventPast7Days";

interface IFeedbackForm {
  risks: Record<string, boolean>;
}

interface IFeedbackFormProps {
  submitLoading?: boolean;
  rowData: IRecentEventsTableRow;
  onSubmitClick: (
    params: IRecentEventsTableRow,
    mitigatedRisk: string[]
  ) => void;
}

const FeedbackDialogForm = ({
  rowData,
  onSubmitClick,
  submitLoading,
}: IFeedbackFormProps): JSX.Element => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IFeedbackForm>({
    defaultValues: {
      risks: MITIGATED_RISKS.reduce((acc, risk) => {
        const mitigatedRisk = rowData.mitigatedRisk?.find(r => r === risk);

        acc[risk] = Boolean(mitigatedRisk) ?? false;

        return acc;
      }, {} as Record<string, boolean>),
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IFeedbackForm> = data => {
    const selectedItems = Object.keys(data.risks).filter(
      key => data.risks[key]
    );

    onSubmitClick(rowData, selectedItems);
  };

  const selectedRisks = watch("risks");

  const isAtLeastOneSelected = Object.values(selectedRisks).some(Boolean);

  const time = rowData?.time;

  const isDisabled = checkIfEventPast7Days(time);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        {MITIGATED_RISKS.map(risk => (
          <Controller
            key={risk}
            name={`risks.${risk}`}
            control={control}
            disabled={isDisabled}
            rules={{
              validate: () =>
                isAtLeastOneSelected || "Please select at least one option.",
            }}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={risk}
              />
            )}
          />
        ))}
      </FormGroup>
      {errors.risks && !isAtLeastOneSelected && (
        <FormHelperText error sx={{ fontSize: "0.75em" }}>
          Please select at least one option.
        </FormHelperText>
      )}
      <Stack
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={2}
        sx={{ paddingTop: 3 }}
      >
        <StyledLoadingButton
          type="submit"
          loading={submitLoading}
          disabled={isDisabled}
          startIcon={null}
          loadingPosition="start"
          variant="contained"
          color="primary"
        >
          Save feedback
        </StyledLoadingButton>
      </Stack>
    </form>
  );
};

export default FeedbackDialogForm;
